import { Link } from "react-router-dom";
import { FadeIn } from "@/components/FadeIn";
import { Ornament } from "@/components/Ornament";
import { useLanguage } from "@/context/LanguageContext";
import { strings } from "@/i18n";

const events = [
  {
    year: "1956",
    title: { fr: "Parisex", en: "Parisex" },
    text: {
      fr: "Marcel Lévy quitte l'Algérie et fonde Parisex à Blois. Les premiers pas d'une aventure familiale.",
      en: "Marcel Lévy leaves Algeria and establishes Parisex in Blois. The first chapter of a family adventure.",
    },
  },
  {
    year: "1962",
    title: { fr: "Tours", en: "Tours" },
    text: {
      fr: "La famille Lévy s'installe à Tours, au 27 rue Marceau. Parisex entre dans une véritable phase de croissance.",
      en: "The Lévy family moves to 27 rue Marceau in Tours, marking the beginning of a new period of growth for Parisex.",
    },
  },
  {
    year: "1978",
    title: { fr: "Roganel est né", en: "Roganel is born" },
    text: {
      fr: (
        <>
          À la fin des années 1970, Parisex devient Roganel — né de
          l'association des prénoms
          <br />
          <span className="font-medium tracking-wide text-bordeaux">ROger, ANdré et MarcEL.</span>
        </>
      ),
      en: (
        <>
          In the late 1970s, Parisex becomes Roganel — a name created from the
          first names
          <br />
          <span className="font-medium tracking-wide text-bordeaux">ROger, ANdré and MarcEL.</span>
        </>
      ),
    },
  },
  {
    year: "1988",
    title: { fr: "Le pari parisien", en: "The Paris adventure" },
    text: {
      fr: "Ouverture de la boutique du 23 rue des Archives, au cœur du Marais. Une façade qui a tout changé.",
      en: "Roganel opens its store at 23 rue des Archives, in the heart of Le Marais. A storefront that would change everything.",
    },
  },
  {
    year: "2000",
    title: { fr: "La marque Roganel", en: "The Roganel brand" },
    text: {
      fr: "Toute la famille s'installe à Paris. Les tee-shirts Roganel et leurs logos emblématiques rencontrent un succès immédiat.",
      en: "The whole family moves to Paris. Roganel T-shirts and their iconic designs quickly become a success.",
    },
  },
  {
    year: "2011",
    title: { fr: "Le renouveau", en: "A new chapter" },
    text: {
      fr: "Karine reprend progressivement les rênes de la boutique. Un nouveau regard, la même exigence.",
      en: "Karine gradually takes the reins of the store. A fresh perspective, with the same commitment to quality.",
    },
  },
  {
    year: "2026",
    title: { fr: "70 ans", en: "70 years" },
    text: {
      fr: "Soixante-dix ans d'histoire. Et demain… de nouvelles pages à écrire.",
      en: "Seventy years of history. And tomorrow… new pages waiting to be written.",
    },
  },
];

export default function Histoire() {
  const { lang } = useLanguage();
  const s = strings.histoire[lang];

  return (
    <div data-testid="histoire-page">
      <section className="mx-auto max-w-4xl px-6 pt-16 text-center md:px-8 md:pt-24">
        <FadeIn>
          <p className="eyebrow" data-testid="histoire-eyebrow">
            {s.eyebrow}
          </p>
          <h1 className="mt-6 font-serif text-4xl font-medium tracking-[0.12em] text-noir sm:text-5xl md:text-6xl" data-testid="histoire-title">
            {s.title}
          </h1>
          <p className="mx-auto mt-6 max-w-lg font-serif text-lg italic leading-relaxed text-noir/70 [text-wrap:pretty] md:text-xl" data-testid="histoire-subtitle">
            {s.subtitleA}
            {lang === "fr" && <br />}
            {s.subtitleB}
          </p>
          <div className="mt-10">
            <Ornament />
          </div>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20 md:px-8 md:py-28" data-testid="histoire-timeline">
        <div className="relative">
          <div className="absolute bottom-0 left-[15px] top-0 w-px bg-bordeaux/25 md:left-1/2" aria-hidden="true" />
          {events.map((e, i) => (
            <FadeIn
              key={e.year}
              className={`relative pb-14 pl-12 md:pb-20 md:w-1/2 md:pl-0 ${
                i % 2 === 0 ? "md:pr-16 md:text-right" : "md:ml-auto md:pl-16"
              }`}
            >
              <span
                className={`absolute top-[13px] h-2.5 w-2.5 rotate-45 bg-bordeaux ${
                  i % 2 === 0
                    ? "left-[10px] md:left-auto md:-right-[5px]"
                    : "left-[10px] md:-left-[5px]"
                }`}
                aria-hidden="true"
              />
              <p className="font-serif text-3xl font-medium text-bordeaux md:text-4xl" data-testid={`timeline-year-${e.year}`}>
                {e.year}
              </p>
              <p className="mt-2 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-noir/60" data-testid={`timeline-title-${e.year}`}>
                {e.title[lang]}
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-relaxed text-noir/70 [text-wrap:pretty] md:text-base" data-testid={`timeline-text-${e.year}`}>
                {e.text[lang]}
              </p>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-bordeaux/10 bg-cream/40" data-testid="histoire-citation">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center md:px-8 md:py-28">
          <FadeIn>
            <blockquote className="mx-auto max-w-2xl font-serif text-2xl italic leading-relaxed text-noir [text-wrap:pretty] md:text-3xl" data-testid="histoire-quote">
              {s.quote}
            </blockquote>
            <p className="mt-8 font-sans text-[11px] uppercase tracking-[0.3em] text-bordeaux" data-testid="histoire-quote-signature">
              {s.signature}
            </p>
            <div className="mt-14">
              <Link to="/70ans" className="btn-outline" data-testid="histoire-cta-livret">
                {s.cta}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
